/**
 * Diagram/Catalog 좌측 선택 상태에 반응하여 kind/domain/relation 메타를 편집하는 사이드 패널.
 *
 * - name (식별자) 은 편집 불가 — rename cascade 는 현재 범위 밖 (추후 지원)
 * - displayName/description/color/icon/category/directionality/inverseOf 편집
 * - 변경은 그 자리에서 draft 에 반영 (onUpdate*)
 * - kind/relation 에 걸린 shape 제약을 `ShapeConstraintList` 로 함께 노출 — 노드가 가질 수 있는
 *   릴레이션 제한이 개별 편집 맥락에서도 눈에 보이도록. 전체 매트릭스 뷰(`ShapeMatrix`) 와 역할 분담.
 *
 * 선택이 없을 때는 힌트 메시지를 표시한다.
 */
import { type ChangeEvent, useMemo, useState } from "react"
import type { DomainDef, KindDef, RelationDef, ShapeDef } from "../../api/admin-client.ts"
import {
	filterIncomingShapes,
	filterOutgoingShapes,
	filterRelationShapes,
	shapeKey
} from "./shape-filters.ts"

/** 필드별 undefined 를 "필드 제거" 의미로 허용하기 위한 patch 타입. useOntologyDraft 의 PatchOf 와 1:1. */
type PatchOf<T> = { [K in keyof T]?: T[K] | undefined }

export type MetaSelection =
	| { readonly type: "kind"; readonly value: KindDef }
	| { readonly type: "domain"; readonly value: DomainDef }
	| { readonly type: "relation"; readonly value: RelationDef }
	| null

interface MetaEditPanelProps {
	readonly selection: MetaSelection
	/** 현재 draft 의 shape 트리플 전체 — kind/relation 메타 폼이 관련 shape 만 골라 표시한다. */
	readonly shapes: ReadonlyArray<ShapeDef>
	readonly onUpdateKind: (name: string, patch: PatchOf<KindDef>) => void
	readonly onUpdateDomain: (name: string, patch: PatchOf<DomainDef>) => void
	readonly onUpdateRelation: (name: string, patch: PatchOf<RelationDef>) => void
	/** 있으면 shape 리스트 항목에 × 버튼 노출. 없으면 읽기 전용 표시. */
	readonly onRemoveShape?: (shape: ShapeDef) => void
	readonly onClose: () => void
}

export function MetaEditPanel(props: MetaEditPanelProps) {
	if (!props.selection) {
		return (
			<div className="meta-edit-panel meta-edit-empty">
				<div className="muted" style={{ fontSize: 13 }}>
					다이어그램 노드 또는 카드를 선택하면 여기서 메타를 편집할 수 있습니다.
				</div>
			</div>
		)
	}

	return (
		<div className="meta-edit-panel">
			<div className="meta-edit-head">
				<span className="meta-edit-kicker">{props.selection.type}</span>
				<code className="inline">{props.selection.value.name}</code>
				<button
					type="button"
					className="btn btn-ghost btn-sm meta-edit-close"
					onClick={props.onClose}
					aria-label="닫기"
				>
					×
				</button>
			</div>

			{props.selection.type === "kind" && (
				<KindMetaForm
					kind={props.selection.value}
					shapes={props.shapes}
					onUpdate={props.onUpdateKind}
					{...(props.onRemoveShape ? { onRemoveShape: props.onRemoveShape } : {})}
				/>
			)}
			{props.selection.type === "domain" && (
				<DomainMetaForm domain={props.selection.value} onUpdate={props.onUpdateDomain} />
			)}
			{props.selection.type === "relation" && (
				<RelationMetaForm
					relation={props.selection.value}
					shapes={props.shapes}
					onUpdate={props.onUpdateRelation}
					{...(props.onRemoveShape ? { onRemoveShape: props.onRemoveShape } : {})}
				/>
			)}
		</div>
	)
}

/** `<input>` 에서 공백만 들어왔을 때 undefined 로 떨어뜨리기 위한 작은 헬퍼. */
function patchString<K extends string>(key: K, value: string): Record<K, string | undefined> {
	return { [key]: value.trim() === "" ? undefined : value } as Record<K, string | undefined>
}

function KindMetaForm(props: {
	readonly kind: KindDef
	readonly shapes: ReadonlyArray<ShapeDef>
	readonly onUpdate: (name: string, patch: PatchOf<KindDef>) => void
	readonly onRemoveShape?: (shape: ShapeDef) => void
}) {
	const { kind, shapes } = props
	const outgoing = filterOutgoingShapes(shapes, kind.name)
	const incoming = filterIncomingShapes(shapes, kind.name)
	return (
		<div className="meta-edit-body">
			<TextField
				label="표시 이름"
				value={kind.displayName}
				onChange={(v) => props.onUpdate(kind.name, { displayName: v })}
			/>
			<TextAreaField
				label="설명"
				value={kind.description ?? ""}
				onChange={(v) => props.onUpdate(kind.name, patchString("description", v))}
			/>
			<div className="meta-edit-row">
				<TextField
					label="색상"
					value={kind.color ?? ""}
					placeholder="#8b949e"
					onChange={(v) => props.onUpdate(kind.name, patchString("color", v))}
				/>
				<TextField
					label="아이콘"
					value={kind.icon ?? ""}
					placeholder="📦"
					onChange={(v) => props.onUpdate(kind.name, patchString("icon", v))}
				/>
			</div>
			<TextField
				label="카테고리"
				value={kind.category ?? ""}
				placeholder="카테고리 (예: SOFTWARE)"
				onChange={(v) => props.onUpdate(kind.name, patchString("category", v))}
			/>
			<ShapeConstraintList
				title="나가는 릴레이션"
				pattern="outgoing"
				contextName={kind.name}
				shapes={outgoing}
				emptyHint="해당 kind 가 source 인 shape 가 없습니다."
				{...(props.onRemoveShape ? { onRemove: props.onRemoveShape } : {})}
			/>
			<ShapeConstraintList
				title="들어오는 릴레이션"
				pattern="incoming"
				contextName={kind.name}
				shapes={incoming}
				emptyHint="해당 kind 를 target 으로 쓰는 shape 가 없습니다."
				{...(props.onRemoveShape ? { onRemove: props.onRemoveShape } : {})}
			/>
		</div>
	)
}

function DomainMetaForm(props: {
	readonly domain: DomainDef
	readonly onUpdate: (name: string, patch: PatchOf<DomainDef>) => void
}) {
	const { domain } = props
	return (
		<div className="meta-edit-body">
			<TextField
				label="표시 이름"
				value={domain.displayName}
				onChange={(v) => props.onUpdate(domain.name, { displayName: v })}
			/>
			<TextAreaField
				label="설명"
				value={domain.description ?? ""}
				onChange={(v) => props.onUpdate(domain.name, patchString("description", v))}
			/>
			<TextField
				label="색상"
				value={domain.color ?? ""}
				placeholder="#3a86ff"
				onChange={(v) => props.onUpdate(domain.name, patchString("color", v))}
			/>
		</div>
	)
}

function RelationMetaForm(props: {
	readonly relation: RelationDef
	readonly shapes: ReadonlyArray<ShapeDef>
	readonly onUpdate: (name: string, patch: PatchOf<RelationDef>) => void
	readonly onRemoveShape?: (shape: ShapeDef) => void
}) {
	const { relation, shapes } = props
	const used = filterRelationShapes(shapes, relation.name)
	return (
		<div className="meta-edit-body">
			<TextField
				label="표시 이름"
				value={relation.displayName}
				onChange={(v) => props.onUpdate(relation.name, { displayName: v })}
			/>
			<TextAreaField
				label="설명"
				value={relation.description ?? ""}
				onChange={(v) => props.onUpdate(relation.name, patchString("description", v))}
			/>
			<div className="meta-edit-row">
				<TextField
					label="색상"
					value={relation.color ?? ""}
					placeholder="#3b82f6"
					onChange={(v) => props.onUpdate(relation.name, patchString("color", v))}
				/>
				<SelectField
					label="방향성"
					value={relation.directionality ?? "directed"}
					options={[
						{ value: "directed", label: "directed (→)" },
						{ value: "undirected", label: "undirected (↔)" }
					]}
					onChange={(v) => props.onUpdate(relation.name, { directionality: v as "directed" | "undirected" })}
				/>
			</div>
			<TextField
				label="역관계"
				value={relation.inverseOf ?? ""}
				placeholder="(역관계 이름)"
				onChange={(v) => props.onUpdate(relation.name, patchString("inverseOf", v))}
			/>
			<ShapeConstraintList
				title="허용된 shape"
				pattern="relation"
				contextName={relation.name}
				shapes={used}
				emptyHint="이 relation 을 사용하는 shape 가 없습니다. 빈 상태면 모든 (kind, kind) 조합이 허용됩니다."
				{...(props.onRemoveShape ? { onRemove: props.onRemoveShape } : {})}
			/>
		</div>
	)
}

/**
 * 선택된 kind / relation 에 연결된 shape 리스트.
 *
 * - pattern 에 따라 표시 형태가 다르다:
 *   - `outgoing` → `→ {targetKind}` (선택된 kind 가 source 일 때)
 *   - `incoming` → `{sourceKind} →` (선택된 kind 가 target 일 때)
 *   - `relation` → `{sourceKind} → {targetKind}` (선택된 relation 을 쓰는 모든 쌍)
 * - onRemove 가 있으면 각 항목 호버 시 × 버튼 노출.
 * - 비어있으면 emptyHint 를 muted 로 표시.
 */
/** shapes 개수가 이 값 이상이면 검색 input 노출 + 리스트 스크롤. 기본 목록이 짧으면 오히려 방해라 임계값. */
const SHAPE_LIST_SEARCH_THRESHOLD = 8

function ShapeConstraintList(props: {
	readonly title: string
	readonly pattern: "outgoing" | "incoming" | "relation"
	readonly contextName: string
	readonly shapes: ReadonlyArray<ShapeDef>
	readonly emptyHint: string
	readonly onRemove?: (shape: ShapeDef) => void
}) {
	const [query, setQuery] = useState("")
	const showSearch = props.shapes.length >= SHAPE_LIST_SEARCH_THRESHOLD
	const filtered = useMemo(() => {
		const q = query.trim().toLowerCase()
		if (!q) return props.shapes
		return props.shapes.filter(
			(s) =>
				s.relation.toLowerCase().includes(q) ||
				s.sourceKind.toLowerCase().includes(q) ||
				s.targetKind.toLowerCase().includes(q)
		)
	}, [props.shapes, query])
	return (
		<div className="meta-edit-shapes">
			<div className="meta-edit-shapes-title">
				{props.title} <span className="muted">({props.shapes.length})</span>
			</div>
			{showSearch && (
				<input
					type="search"
					className="meta-edit-shapes-search"
					placeholder="relation · source · target 검색"
					value={query}
					onChange={(e) => setQuery(e.target.value)}
					spellCheck={false}
					aria-label={`${props.title} 검색`}
				/>
			)}
			{props.shapes.length === 0 ? (
				<div className="meta-edit-shapes-empty">{props.emptyHint}</div>
			) : (
				<ul className={`meta-edit-shapes-list${showSearch ? " is-scrollable" : ""}`}>
					{filtered.map((s) => (
						<li key={shapeKey(s)} className="meta-edit-shape-item">
							<ShapeInline pattern={props.pattern} shape={s} contextName={props.contextName} />
							{props.onRemove && (
								<button
									type="button"
									className="meta-edit-shape-remove"
									aria-label={`shape 제거 ${shapeKey(s)}`}
									onClick={() => props.onRemove?.(s)}
								>
									×
								</button>
							)}
						</li>
					))}
					{showSearch && filtered.length === 0 && <li className="meta-edit-shapes-empty">검색 결과 없음</li>}
				</ul>
			)}
		</div>
	)
}

/** shape 트리플 인라인 표시 — pattern 에 따라 어느 kind 를 생략할지 결정. */
function ShapeInline(props: {
	readonly pattern: "outgoing" | "incoming" | "relation"
	readonly shape: ShapeDef
	readonly contextName: string
}) {
	const { shape, pattern } = props
	if (pattern === "outgoing") {
		return (
			<span className="meta-edit-shape-triple">
				<code className="inline">{shape.relation}</code>
				<span className="muted">→</span>
				<code className="inline">{shape.targetKind}</code>
			</span>
		)
	}
	if (pattern === "incoming") {
		return (
			<span className="meta-edit-shape-triple">
				<code className="inline">{shape.sourceKind}</code>
				<span className="muted">—</span>
				<code className="inline">{shape.relation}</code>
				<span className="muted">→</span>
			</span>
		)
	}
	// relation pattern — source → target 풀 표시
	return (
		<span className="meta-edit-shape-triple">
			<code className="inline">{shape.sourceKind}</code>
			<span className="muted">→</span>
			<code className="inline">{shape.targetKind}</code>
		</span>
	)
}

function TextField(props: {
	readonly label: string
	readonly value: string
	readonly placeholder?: string
	readonly onChange: (next: string) => void
}) {
	return (
		<label className="field">
			<span className="field-label">{props.label}</span>
			<input
				className="input"
				type="text"
				value={props.value}
				placeholder={props.placeholder ?? ""}
				onChange={(e: ChangeEvent<HTMLInputElement>) => props.onChange(e.target.value)}
				spellCheck={false}
			/>
		</label>
	)
}

function TextAreaField(props: {
	readonly label: string
	readonly value: string
	readonly onChange: (next: string) => void
}) {
	return (
		<label className="field">
			<span className="field-label">{props.label}</span>
			<textarea
				className="input"
				value={props.value}
				onChange={(e: ChangeEvent<HTMLTextAreaElement>) => props.onChange(e.target.value)}
				rows={3}
				style={{ minHeight: 70 }}
			/>
		</label>
	)
}

function SelectField(props: {
	readonly label: string
	readonly value: string
	readonly options: ReadonlyArray<{ readonly value: string; readonly label: string }>
	readonly onChange: (next: string) => void
}) {
	return (
		<label className="field">
			<span className="field-label">{props.label}</span>
			<select
				className="input select"
				value={props.value}
				onChange={(e: ChangeEvent<HTMLSelectElement>) => props.onChange(e.target.value)}
			>
				{props.options.map((opt) => (
					<option key={opt.value} value={opt.value}>
						{opt.label}
					</option>
				))}
			</select>
		</label>
	)
}
